import React from "react"
import { graphql } from "gatsby"
import { Layout } from "@bw/layouts"
import { Section, Seo, Button, Grid } from "@bw/bits"
import * as modules from "@bw/modules/resolvers"
import * as Icons from "@bw/icons"
// parse is used to ensure that HTML sent by the CMS is converted to React components
import parse from "html-react-parser"
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion"

const TopicTemplate = ({ data, pageContext }) => {
  const { page, faqsParents = { nodes: [] }, faqsJeune = { nodes: [] } } = data

  /*
   * Loading the Contents modules, ex Content or CallToAction, TeasersLatest
   */
  const contents = page.content
    .filter(module => module !== null)
    .map((module, i) => {
      const LoadedModule = modules[module.__typename.replace("Module", "")]
      if (typeof LoadedModule === "undefined") {
        return null
      }

      if (module.buttonType?.value === "url" && module.buttonUrl) {
        module.button = (
          <Button
            label={module.buttonText}
            href={module.buttonUrl}
            external={module.buttonIsExternal}
            primary
          />
        )
      }

      if (module.buttonType?.value === "entry" && module.buttonEntry) {
        module.button = (
          <Button
            label={module.buttonText}
            to={module.buttonEntry.url}
            primary
          />
        )
      }

      module.buttonPosition = module.buttonPosition?.value

      return <LoadedModule key={i} {...module} />
    })

  return (
    <Layout {...{ pageContext }}>
      <Seo
        title={page.metaTitle || page.title}
        description={page.metaDescription}
        meta={[
          {
            name: "robots",
            content: page.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      {/*
       * Loading the Header module, ex Hero or PageHeader
       */}
      {page.header
        .filter(module => module !== null)
        .map((module, i) => {
          const LoadedModule = modules[module.__typename.replace("Module", "")]
          if (typeof LoadedModule === "undefined") {
            return null
          }

          if (!module.title) {
            module.title = page.title
          }

          if (module.__typename === "HeroModule") {
            if (page.icon?.value) {
              page.icon.value =
                page.icon.value.charAt(0).toUpperCase() +
                page.icon.value.slice(1).toLowerCase().replaceAll("-", "")
            }
            const Icon =
              typeof Icons[page.icon.value] !== "undefined"
                ? Icons[page.icon.value]
                : Icons.Vide

            module.icon = <Icon width="80px" height="80px" />
          }

          return <LoadedModule key={i} {...module} />
        })}

      {contents}

      {(faqsJeune.nodes.length > 0 || faqsParents.nodes.length > 0) && (
        <Section title="Questions fréquemment posées">
          <Grid>
            <div>
              {faqsParents.nodes.length > 0 && (
                <>
                  <h3>Parents</h3>
                  <Accordion allowMultipleExpanded allowZeroExpanded>
                    {faqsParents.nodes.map((faq, i) => (
                      <AccordionItem key={i}>
                        <AccordionItemHeading>
                          <AccordionItemButton>{faq.title}</AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel>
                          {parse(faq.content)}
                        </AccordionItemPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
            </div>
            <div>
              {faqsParents.nodes.length > 0 && (
                <>
                  <h3>Enfants</h3>
                  <Accordion allowMultipleExpanded allowZeroExpanded>
                    {faqsJeune.nodes.map((faq, i) => (
                      <AccordionItem key={i}>
                        <AccordionItemHeading>
                          <AccordionItemButton>{faq.title}</AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel>
                          {parse(faq.content)}
                        </AccordionItemPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
            </div>
          </Grid>
        </Section>
      )}
    </Layout>
  )
}

export default TopicTemplate

export const pageQuery = graphql`
  query topicQuery($id: String!) {
    page: collectionTopics(id: { eq: $id }) {
      url
      title
      metaTitle
      metaDescription
      image {
        url
      }
      doNotIndex

      icon {
        value
      }

      teaserImage {
        url
      }
      header {
        __typename
        suptitle
        title
        subtitle

        ... on HeroModule {
          ...HeroModuleFragment
        }
      }
      content {
        __typename
        suptitle
        title
        subtitle
        backgroundVariant
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }

        ... on CallToActionModule {
          ...CallToActionModuleFragment
        }
        ... on ContentModule {
          ...ContentModuleFragment
        }
        ... on ContentWithImageModule {
          ...ContentWithImageModuleFragment
        }
        ... on GalleryModule {
          ...GalleryModuleFragment
        }
        ... on ContentOnColumnsModule {
          ...ContentOnColumnsModuleFragment
        }
        ... on DownloadsModule {
          ...DownloadsModuleFragment
        }
        ... on TeasersLatestModule {
          ...TeasersLatestModuleFragment
        }
        ... on TeasersCustomModule {
          ...TeasersCustomModuleFragment
        }
        ... on TeasersManualModule {
          ...TeasersManualModuleFragment
        }
        ... on LogoGridModule {
          ...LogoGridModuleFragment
        }
        ... on ImageModule {
          ...ImageModuleFragment
        }
        ... on ImagesLinksModule {
          ...ImagesLinksModuleFragment
        }
        ... on VideoModule {
          ...VideoModuleFragment
        }
        ... on FormModule {
          ...FormModuleFragment
        }
      }
    }

    faqsParents: allCollectionFaqs(
      filter: {
        topics: { elemMatch: { id: { eq: $id } } }
        target: { slug: { eq: "parents" } }
      }
    ) {
      nodes {
        title
        content
      }
    }

    faqsJeune: allCollectionFaqs(
      filter: {
        topics: { elemMatch: { id: { eq: $id } } }
        target: { slug: { eq: "jeune" } }
      }
    ) {
      nodes {
        title
        content
      }
    }
  }
`
